import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { useReducedMotion } from '@/lib/motion-presets';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';

interface SettingsWidgetCardProps {
	id: string;
	icon: LucideIcon;
	title: string;
	description: string;
	titleClassName?: string;
	iconWrapperClassName?: string;
	cardClassName?: string;
	defaultOpen?: boolean;
	children: ReactNode;
}

export default function SettingsWidgetCard({
	id,
	icon: Icon,
	title,
	description,
	titleClassName,
	iconWrapperClassName,
	cardClassName,
	defaultOpen = false,
	children,
}: SettingsWidgetCardProps) {
	const [open, setOpen] = useState(defaultOpen);
	const reducedMotion = useReducedMotion();
	const contentId = `settings-widget-${id}`;

	return (
		<Card className={cn('gap-0 py-0 shadow-sm overflow-hidden', cardClassName)}>
			<button
				type='button'
				className='border-b px-4 py-4 w-full text-left transition-colors hover:bg-muted/40'
				aria-expanded={open}
				aria-controls={contentId}
				onClick={() => setOpen(v => !v)}
			>
				<div className='flex items-center justify-between gap-3 w-full'>
					<div className='flex items-center gap-3 min-w-0'>
						<div className={cn('rounded-lg p-2 shrink-0', iconWrapperClassName)}>
							<Icon className={titleClassName} size={20} />
						</div>
						<div className='flex flex-col gap-1 min-w-0'>
							<CardTitle className={titleClassName}>{title}</CardTitle>
							<CardDescription>{description}</CardDescription>
						</div>
					</div>
					<ChevronDownIcon
						size={18}
						aria-hidden
						className={cn('shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
					/>
				</div>
			</button>

			<div
				className={cn(
					'grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out',
					open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
					reducedMotion && 'transition-none',
					!open && 'pointer-events-none',
				)}
				aria-hidden={!open}
			>
				<div id={contentId} className='overflow-hidden min-h-0'>
					{children}
				</div>
			</div>
		</Card>
	);
}
